import { AuthService } from 'phoenix/services';
import { TimeZone } from './timeZones';

export type ITimeZoneService = TimeZoneService;

/**
 * @ngdoc service
 * @name sharebase.timezones.sbTimeZoneService
 * 
 * @description
 * Exposes methods for configuring timezones at the deployment level.
 */
export class TimeZoneService {
    public static readonly $inject = [
        '$q',
        '$http',
        'sbTimeZones',
        'pxUriPathService',
        'pxAuthService',
        'gettextCatalog'
    ]

    private readonly deploymentTimeZoneUrl: string;
    private readonly userTimeZoneUrl: string;

    constructor(
        private readonly $q: ng.IQService,
        private readonly $http: ng.IHttpService,
        private readonly timeZones: TimeZone[],
        uriPathService,
        authService: AuthService,
        private readonly gettextCatalog
    ) {
        this.deploymentTimeZoneUrl = uriPathService.resolvePath(`/admin/${authService.deploymentId}/timezone`);
        this.userTimeZoneUrl = uriPathService.resolvePath('/timezone');
    }

    /**
     * @ngdoc method
     * @name sbTimeZoneService#getDeploymentTimeZone
     * @methodOf sharebase.timezones.sbTimeZoneService
     * @kind method
     * 
     * @description
     * Get the configured time zone for the current' users deployment.
     * 
     * @returns {Deferred} A promise that resolves with the configured time zone or null if
     * the deployment does not have one set.
     */
    public getDeploymentTimeZone(): ng.IPromise<TimeZone> {

        return this.getTimeZone(this.deploymentTimeZoneUrl);
    }

    /**
     * @ngdoc method
     * @name sbTimeZoneService#setDeploymentTimeZone
     * @methodOf sharebase.timezones.sbTimeZoneService
     * @kind method
     * 
     * @description
     * Save the given timezone at the deployment level.
     * 
     * @param tz The timezone to save
     * 
     * @returns {Deferred} An angular.js http promise
     */
    public setDeploymentTimeZone(tz: TimeZone): ng.IPromise<any> {
        return this.setTimeZone(this.deploymentTimeZoneUrl, tz);
    }

    /**
     * @ngdoc method
     * @name sbTimeZoneService#getUserTimeZone
     * @methodOf sharebase.timezones.sbTimeZoneService
     * @kind method
     * 
     * @description
     * Gets the effective configured time zone for the user. If there is none, the method will return null.
     * 
     * @returns {Deferred} A promise that resolves with the effective configured time zone or null if no
     * time zone is set.
     */
    public getUserTimeZone(): ng.IPromise<TimeZone> {
        return this.getTimeZone(this.userTimeZoneUrl);
    }

    /**
     * @ngdoc method
     * @name sbTimeZoneService#setUserTimeZone
     * @methodOf sharebase.timezones.sbTimeZoneService
     * @kind method
     * 
     * @description
     * Save the given timezone at the user level.
     * 
     * @param tz The timezone to save
     * 
     * @returns {Deferred} An angular.js http promise
     */
    public setUserTimeZone(tz: TimeZone): ng.IPromise<any> {
        return this.setTimeZone(this.userTimeZoneUrl, tz);
    }

    private setTimeZone(url: string, tz: TimeZone): ng.IPromise<any> {
        if (tz) {
            return this.$http.put(url, JSON.stringify(tz.value));
        }
        else {
            return this.$q.reject(new Error('A time zone must be specified to save'));
        }
    }

    private getTimeZone(url: string): ng.IPromise<TimeZone> {
        return this.$http.get(url)
            .then((res: any) => res.data.TimeZone as string)
            .then(timezoneId => {
                let timeZone: TimeZone = null;
                if (timezoneId) {
                    let accepted = this.timeZones.filter(tz => tz.value === timezoneId);
                    if (accepted.length > 0) {
                        timeZone = accepted[0];
                    }
                }

                return timeZone;
            });
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/timeZoneService.ts